import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { 
    ChevronLeft, ExternalLink, Star, DollarSign, Users, TrendingUp,
    BookOpen, Megaphone, Plus, Calendar, BarChart3, Clock, Check, X,
    Loader2, Sparkles, Target, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { promotionsApi, booksApi } from '../lib/api';

const BookPromotion = ({ onBack, onNavigate }) => {
    const [selectedTier, setSelectedTier] = useState('all');
    const [services, setServices] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [stats, setStats] = useState(null);
    const [books, setBooks] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('campaigns');
    
    // Campaign creation state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newCampaign, setNewCampaign] = useState({
        book_id: '',
        campaign_name: '',
        services: [],
        promotion_type: 'discount',
        discount_price: '',
        start_date: '',
        end_date: '',
        budget: '',
        notes: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [servicesRes, campaignsRes, statsRes, booksRes] = await Promise.all([
                promotionsApi.getServices(),
                promotionsApi.getCampaigns(),
                promotionsApi.getStats(),
                booksApi.list()
            ]);
            
            setServices(servicesRes.data.services || []);
            setCampaigns(campaignsRes.data.campaigns || []);
            setStats(statsRes.data);
            setBooks(booksRes.data || []);
        } catch (error) {
            console.error('Failed to load promotion data:', error);
            toast.error('Failed to load promotion data');
        } finally {
            setLoading(false);
        }
    };

    const loadRecommendations = async (bookId) => {
        if (!bookId) {
            setRecommendations([]);
            return;
        }
        try {
            const res = await promotionsApi.getRecommendations(bookId);
            setRecommendations(res.data.recommendations || []);
        } catch (error) {
            console.error('Failed to load recommendations:', error);
        }
    };

    const handleCreateCampaign = async () => {
        if (!newCampaign.book_id || !newCampaign.campaign_name || newCampaign.services.length === 0) {
            toast.error('Please fill in all required fields');
            return;
        }

        setCreating(true);
        try {
            const campaignData = {
                ...newCampaign,
                discount_price: newCampaign.discount_price ? parseFloat(newCampaign.discount_price) : null,
                budget: newCampaign.budget ? parseFloat(newCampaign.budget) : null
            };
            
            await promotionsApi.createCampaign(campaignData);
            toast.success('Campaign created successfully!');
            setShowCreateDialog(false);
            setNewCampaign({
                book_id: '',
                campaign_name: '',
                services: [],
                promotion_type: 'discount',
                discount_price: '',
                start_date: '',
                end_date: '',
                budget: '',
                notes: ''
            });
            loadData();
        } catch (error) {
            toast.error('Failed to create campaign');
        } finally {
            setCreating(false);
        }
    };

    const handleUpdateSubmission = async (submissionId, status) => {
        try {
            await promotionsApi.updateSubmission(submissionId, { status });
            toast.success(`Submission marked as ${status}`);
            loadData();
        } catch (error) {
            toast.error('Failed to update submission');
        }
    };

    const handleDeleteCampaign = async (campaignId) => {
        if (!window.confirm('Are you sure you want to delete this campaign?')) return;
        
        try {
            await promotionsApi.deleteCampaign(campaignId);
            toast.success('Campaign deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete campaign');
        }
    };

    const toggleServiceSelection = (serviceId) => {
        setNewCampaign(prev => ({
            ...prev,
            services: prev.services.includes(serviceId)
                ? prev.services.filter(id => id !== serviceId)
                : [...prev.services, serviceId]
        }));
    };

    const tierFilter = selectedTier === 'all' 
        ? services 
        : services.filter(s => s.tier === selectedTier);

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-500';
            case 'submitted': return 'bg-blue-500';
            case 'rejected': return 'bg-red-500';
            default: return 'bg-gray-400';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                        <div>
                            <h1 className="font-serif text-2xl font-semibold">Book Promotion Hub</h1>
                            <p className="text-sm text-muted-foreground">Manage your promotion campaigns</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onNavigate && onNavigate('credits')}
                            data-testid="promotion-buy-credits"
                        >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Buy Credits
                        </Button>
                        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                            <DialogTrigger asChild>
                                <Button data-testid="create-campaign-btn">
                                    <Plus className="h-4 w-4 mr-2" />
                                    New Campaign
                                </Button>
                            </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-serif">Create Promotion Campaign</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Book *</Label>
                                        <Select 
                                            value={newCampaign.book_id} 
                                            onValueChange={(value) => {
                                                setNewCampaign(prev => ({ ...prev, book_id: value }));
                                                loadRecommendations(value);
                                            }}
                                        >
                                            <SelectTrigger data-testid="campaign-book-select">
                                                <SelectValue placeholder="Select a book" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {books.map(book => (
                                                    <SelectItem key={book.id} value={book.id}>
                                                        {book.title}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Campaign Name *</Label>
                                        <Input
                                            value={newCampaign.campaign_name}
                                            onChange={(e) => setNewCampaign(prev => ({ ...prev, campaign_name: e.target.value }))}
                                            placeholder="e.g., Spring Launch 2026"
                                            data-testid="campaign-name-input"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Promotion Type</Label>
                                        <Select 
                                            value={newCampaign.promotion_type}
                                            onValueChange={(value) => setNewCampaign(prev => ({ ...prev, promotion_type: value }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="free">Free Promotion</SelectItem>
                                                <SelectItem value="discount">Discount</SelectItem>
                                                <SelectItem value="full_price">Full Price</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {newCampaign.promotion_type === 'discount' && (
                                        <div>
                                            <Label>Discount Price ($)</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={newCampaign.discount_price}
                                                onChange={(e) => setNewCampaign(prev => ({ ...prev, discount_price: e.target.value }))}
                                                placeholder="0.99"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Start Date</Label>
                                        <Input
                                            type="date"
                                            value={newCampaign.start_date}
                                            onChange={(e) => setNewCampaign(prev => ({ ...prev, start_date: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <Label>End Date</Label>
                                        <Input
                                            type="date"
                                            value={newCampaign.end_date}
                                            onChange={(e) => setNewCampaign(prev => ({ ...prev, end_date: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label>Budget ($)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={newCampaign.budget}
                                        onChange={(e) => setNewCampaign(prev => ({ ...prev, budget: e.target.value }))}
                                        placeholder="Optional"
                                    />
                                </div>

                                {recommendations.length > 0 && (
                                    <div className="p-3 bg-accent/10 rounded-lg">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Sparkles className="h-4 w-4 text-accent" />
                                            <span className="text-sm font-medium">Recommended Services</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {recommendations.slice(0, 4).map(rec => (
                                                <Badge 
                                                    key={rec.service_id} 
                                                    variant={newCampaign.services.includes(rec.service_id) ? "default" : "outline"}
                                                    className="cursor-pointer"
                                                    onClick={() => toggleServiceSelection(rec.service_id)}
                                                >
                                                    {rec.name}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <Label>Select Promotion Services *</Label>
                                    <ScrollArea className="h-48 border rounded-lg p-3 mt-2">
                                        <div className="space-y-2">
                                            {services.map(service => (
                                                <div 
                                                    key={service.id}
                                                    className="flex items-center space-x-3 p-2 hover:bg-muted/50 rounded"
                                                >
                                                    <Checkbox
                                                        id={service.id}
                                                        checked={newCampaign.services.includes(service.id)}
                                                        onCheckedChange={() => toggleServiceSelection(service.id)}
                                                    />
                                                    <label htmlFor={service.id} className="flex-1 cursor-pointer">
                                                        <div className="flex items-center justify-between">
                                                            <span className="font-medium text-sm">{service.name}</span>
                                                            <div className="flex items-center gap-2">
                                                                <Badge variant="outline" className="text-xs">{service.tier}</Badge>
                                                                <span className="text-xs text-muted-foreground">{service.cost_range}</span>
                                                            </div>
                                                        </div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {newCampaign.services.length} service(s) selected
                                    </p>
                                </div>

                                <div>
                                    <Label>Notes</Label>
                                    <Input
                                        value={newCampaign.notes}
                                        onChange={(e) => setNewCampaign(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Any additional notes..."
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                                <Button onClick={handleCreateCampaign} disabled={creating}>
                                    {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                                    Create Campaign
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8">
                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-4 gap-4 mb-8">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-accent/10 rounded-lg">
                                        <Target className="h-5 w-5 text-accent" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.total_campaigns}</p>
                                        <p className="text-sm text-muted-foreground">Total Campaigns</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-500/10 rounded-lg">
                                        <RefreshCw className="h-5 w-5 text-green-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.active_campaigns}</p>
                                        <p className="text-sm text-muted-foreground">Active</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-500/10 rounded-lg">
                                        <Megaphone className="h-5 w-5 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.total_submissions}</p>
                                        <p className="text-sm text-muted-foreground">Submissions</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-accent/10 rounded-lg">
                                        <BarChart3 className="h-5 w-5 text-accent" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.approval_rate}%</p>
                                        <p className="text-sm text-muted-foreground">Approval Rate</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="campaigns" data-testid="tab-campaigns">
                            <Target className="h-4 w-4 mr-2" />
                            My Campaigns
                        </TabsTrigger>
                        <TabsTrigger value="services" data-testid="tab-services">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Promotion Services
                        </TabsTrigger>
                        <TabsTrigger value="guide" data-testid="tab-guide">
                            <BookOpen className="h-4 w-4 mr-2" />
                            Strategy Guide
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="campaigns">
                        {campaigns.length === 0 ? (
                            <Card className="text-center py-12">
                                <CardContent>
                                    <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <h3 className="font-serif text-xl mb-2">No Campaigns Yet</h3>
                                    <p className="text-muted-foreground mb-4">
                                        Create your first promotion campaign to start reaching readers.
                                    </p>
                                    <Button onClick={() => setShowCreateDialog(true)}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Create First Campaign
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                {campaigns.map(campaign => {
                                    const book = books.find(b => b.id === campaign.book_id);
                                    return (
                                        <Card key={campaign.id} data-testid={`campaign-${campaign.id}`}>
                                            <CardHeader className="pb-2">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <CardTitle className="font-serif">{campaign.campaign_name}</CardTitle>
                                                        <CardDescription>
                                                            {book?.title || 'Unknown Book'} • {campaign.promotion_type}
                                                            {campaign.discount_price && ` • $${campaign.discount_price}`}
                                                        </CardDescription>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
                                                            {campaign.status}
                                                        </Badge>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={() => handleDeleteCampaign(campaign.id)}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="h-4 w-4" />
                                                        {campaign.start_date || 'No date'} - {campaign.end_date || 'No date'}
                                                    </div>
                                                    {campaign.budget && (
                                                        <div className="flex items-center gap-1">
                                                            <DollarSign className="h-4 w-4" />
                                                            Budget: ${campaign.budget}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="text-xs">Submissions</Label>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                        {campaign.submissions?.map(sub => {
                                                            const service = services.find(s => s.id === sub.service_id);
                                                            return (
                                                                <div 
                                                                    key={sub.id}
                                                                    className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={`w-2 h-2 rounded-full ${getStatusColor(sub.status)}`} />
                                                                        <span className="text-sm truncate">{service?.name || sub.service_id}</span>
                                                                    </div>
                                                                    <Select
                                                                        value={sub.status}
                                                                        onValueChange={(value) => handleUpdateSubmission(sub.id, value)}
                                                                    >
                                                                        <SelectTrigger className="h-7 w-24 text-xs">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="pending">Pending</SelectItem>
                                                                            <SelectItem value="submitted">Submitted</SelectItem>
                                                                            <SelectItem value="approved">Approved</SelectItem>
                                                                            <SelectItem value="rejected">Rejected</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="services">
                        {/* Tier Filter */}
                        <div className="flex gap-2 mb-6">
                            {['all', 'premium', 'standard', 'budget'].map((tier) => (
                                <Button
                                    key={tier}
                                    variant={selectedTier === tier ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setSelectedTier(tier)}
                                    data-testid={`filter-${tier}`}
                                >
                                    {tier === 'all' ? 'All Services' : tier.charAt(0).toUpperCase() + tier.slice(1)}
                                </Button>
                            ))}
                        </div>

                        {/* Services Grid */}
                        <div className="grid md:grid-cols-2 gap-4">
                            {tierFilter.map((service) => (
                                <Card key={service.id} className="hover-lift" data-testid={`service-${service.id}`}>
                                    <CardHeader className="pb-2">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <CardTitle className="font-serif text-lg">{service.name}</CardTitle>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Badge variant={
                                                        service.tier === 'premium' ? 'default' : 
                                                        service.tier === 'standard' ? 'secondary' : 'outline'
                                                    }>
                                                        {service.tier}
                                                    </Badge>
                                                    {service.api_available && (
                                                        <Badge variant="outline" className="text-xs">API Available</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                                            <div className="flex items-center gap-2">
                                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                                                <span>{service.cost_range}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Users className="h-4 w-4 text-muted-foreground" />
                                                <span>{service.reach}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between pt-3 border-t border-border">
                                            <a href={service.submission_url} target="_blank" rel="noopener noreferrer">
                                                <Button size="sm" variant="outline">
                                                    Visit <ExternalLink className="h-3 w-3 ml-1" />
                                                </Button>
                                            </a>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="guide">
                        <div className="max-w-3xl mx-auto">
                            <Card className="mb-6">
                                <CardHeader>
                                    <CardTitle className="font-serif">Book Promotion Strategy Guide</CardTitle>
                                    <CardDescription>Expert-recommended approach for maximum ROI</CardDescription>
                                </CardHeader>
                                <CardContent className="prose prose-sm max-w-none">
                                    <h3 className="font-serif">Phase 1: Pre-Launch (4-6 weeks before)</h3>
                                    <ul>
                                        <li>Build an email list of interested readers</li>
                                        <li>Gather ARC (Advance Reader Copy) reviewers</li>
                                        <li>Optimize your book description with keywords</li>
                                        <li>Prepare social media graphics and content</li>
                                    </ul>

                                    <h3 className="font-serif">Phase 2: Launch Week</h3>
                                    <ul>
                                        <li>Coordinate promotion site submissions (stack 3-5 on launch day)</li>
                                        <li>Run a free or $0.99 promotion for maximum visibility</li>
                                        <li>Recommended combo: Freebooksy + ENT + Robin Reads</li>
                                        <li>Apply to BookBub Featured Deals</li>
                                    </ul>

                                    <h3 className="font-serif">Phase 3: Ongoing Promotion</h3>
                                    <ul>
                                        <li>Run quarterly price promotions with stacked sites</li>
                                        <li>Use BookBub Ads for continuous low-spend visibility</li>
                                        <li>Quality over frequency: 4 big promotions/year beats 12 small ones</li>
                                    </ul>

                                    <h3 className="font-serif">Budget Recommendations</h3>
                                    <div className="not-prose grid grid-cols-3 gap-4 mt-4">
                                        <Card>
                                            <CardContent className="pt-4 text-center">
                                                <p className="font-bold text-lg">$50-100</p>
                                                <p className="text-sm text-muted-foreground">Starter Budget</p>
                                                <p className="text-xs mt-2">2-3 budget services</p>
                                            </CardContent>
                                        </Card>
                                        <Card>
                                            <CardContent className="pt-4 text-center">
                                                <p className="font-bold text-lg">$200-400</p>
                                                <p className="text-sm text-muted-foreground">Recommended</p>
                                                <p className="text-xs mt-2">4-5 mixed tier services</p>
                                            </CardContent>
                                        </Card>
                                        <Card>
                                            <CardContent className="pt-4 text-center">
                                                <p className="font-bold text-lg">$500+</p>
                                                <p className="text-sm text-muted-foreground">Premium</p>
                                                <p className="text-xs mt-2">Include BookBub + Ads</p>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default BookPromotion;
